import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import SearchBar from './SearchBar';
import { api, withErrorHandling } from '../services/api';

const CityLandingPage = () => {
  const { citySlug } = useParams();
  const [cityData, setCityData] = useState(null);
  const [cityImage, setCityImage] = useState(null);
  const [loading, setLoading] = useState(true);

  // Convert city slug to display name
  const cityName = citySlug
    .split('-')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        
        // Fetch city image and listings in parallel
        const [image, listings] = await Promise.all([
          withErrorHandling(() => api.getCityImage(citySlug)),
          withErrorHandling(() => api.getCityListings(citySlug))
        ]);
        
        setCityImage(image);
        setCityData({ providers: listings });
      } catch (error) {
        console.error('Error fetching city data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [citySlug]);

  // Update meta tags for SEO
  useEffect(() => {
    document.title = `Commercial Kitchen Repair Services in ${cityName} | HotSide Repairs`;
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 
        `Find trusted commercial cooking equipment repair services in ${cityName}. 24/7 emergency repairs, certified technicians, and fast response times.`
      );
    }
  }, [cityName]);

  if (loading) {
    return <div className="loading">Loading...</div>;
  }

  return (
    <div className="city-landing-page">
      <section 
        className="city-hero"
        style={{
          backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.5), rgba(0, 0, 0, 0.7)), url(${cityImage?.url})`
        }}
      >
        <div className="hero-content">
          <h1>Commercial Kitchen Equipment Repair in {cityName}</h1>
          <p className="subtitle">
            Connect with certified technicians for fast, reliable repairs
          </p>
          <div className="search-container">
            <SearchBar placeholder={`Search repair services in ${cityName}...`} />
          </div>
          {cityImage?.credit && (
            <div className="image-credit">
              Photo by <a href={cityImage.credit.link} target="_blank" rel="noopener noreferrer">
                {cityImage.credit.name}
              </a> on <a href="https://unsplash.com" target="_blank" rel="noopener noreferrer">
                Unsplash
              </a>
            </div>
          )}
        </div>
      </section>

      <section className="service-providers card">
        <h2>Top Rated Service Providers in {cityName}</h2>
        <div className="providers-grid">
          {cityData.providers.map(provider => (
            <div key={provider.id} className="provider-card">
              <h3>{provider.name}</h3>
              <div className="rating">
                <span className="stars">{'★'.repeat(Math.floor(provider.rating))}</span>
                <span className="rating-text">
                  {provider.rating} ({provider.reviewCount} reviews)
                </span>
              </div>
              <div className="services">
                <h4>Services:</h4>
                <ul>
                  {provider.services.map(service => (
                    <li key={service}>{service}</li>
                  ))}
                </ul>
              </div>
              <div className="response-time">
                <strong>Response Time:</strong> {provider.response_time}
              </div>
              <button className="button">Contact Provider</button>
            </div>
          ))}
        </div>
      </section>

      <section className="city-info card">
        <h2>Commercial Kitchen Repair Services in {cityName}</h2>
        <p>
          Need urgent repairs for your commercial kitchen equipment in {cityName}? 
          Our network of certified technicians specializes in repairing and maintaining 
          all types of commercial cooking equipment, including ovens, refrigerators, 
          fryers, grills, and more.
        </p>
        <div className="features-grid">
          <div className="feature">
            <h3>24/7 Emergency Service</h3>
            <p>Available around the clock for urgent repairs</p>
          </div>
          <div className="feature">
            <h3>Certified Technicians</h3>
            <p>Experienced professionals you can trust</p>
          </div>
          <div className="feature">
            <h3>Fast Response</h3>
            <p>Quick service to minimize downtime</p>
          </div>
          <div className="feature">
            <h3>All Equipment Types</h3>
            <p>Comprehensive repair services</p>
          </div>
        </div>
      </section>

      <style>
        {`
          .city-landing-page {
            min-height: 100vh;
          }

          .city-hero {
            background-size: cover;
            background-position: center;
            color: white;
            padding: 4rem 1rem;
            text-align: center;
            position: relative;
          }

          .hero-content {
            max-width: 800px;
            margin: 0 auto;
            position: relative;
            z-index: 1;
          }

          .hero-content h1 {
            font-size: 2.5rem;
            margin-bottom: 1rem;
            animation: fadeInUp 0.8s ease;
          }

          .subtitle {
            font-size: 1.25rem;
            margin-bottom: 2rem;
            opacity: 0.9;
            animation: fadeInUp 0.8s ease 0.2s both;
          }

          .search-container {
            max-width: 600px;
            margin: 0 auto 2rem;
            animation: fadeInUp 0.8s ease 0.4s both;
          }

          .image-credit {
            position: absolute;
            bottom: 1rem;
            right: 1rem;
            font-size: 0.8rem;
            opacity: 0.8;
          }

          .image-credit a {
            color: white;
            text-decoration: underline;
          }

          .providers-grid {
            display: grid;
            grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
            gap: 1.5rem;
            margin-top: 1.5rem;
          }

          .provider-card {
            padding: 1.5rem;
            border: 1px solid var(--border-color);
            border-radius: 8px;
            background: white;
            transition: transform var(--transition-normal), box-shadow var(--transition-normal);
          }

          .provider-card:hover {
            transform: translateY(-5px);
            box-shadow: var(--shadow-md);
          }

          .rating {
            margin: 1rem 0;
          }

          .stars {
            color: #ffd700;
            margin-right: 0.5rem;
          }

          .services ul {
            list-style: none;
            padding: 0;
            margin: 0.5rem 0;
          }

          .services li {
            margin: 0.25rem 0;
            padding: 0.25rem 0.5rem;
            background: var(--background-color);
            border-radius: 4px;
            display: inline-block;
            margin-right: 0.5rem;
          }

          .response-time {
            margin: 1rem 0;
            color: #666;
          }

          .features-grid {
            display: grid;
            grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
            gap: 1.5rem;
            margin-top: 1.5rem;
          }

          .feature {
            text-align: center;
            padding: 1.5rem;
            background: var(--background-color);
            border-radius: var(--border-radius-md);
          }

          .feature h3 {
            margin-bottom: 0.5rem;
            color: var(--primary-color);
          }

          @keyframes fadeInUp {
            from {
              opacity: 0;
              transform: translateY(20px);
            }
            to {
              opacity: 1;
              transform: translateY(0);
            }
          }

          @media (max-width: 768px) {
            .hero-content h1 {
              font-size: 2rem;
            }

            .providers-grid {
              grid-template-columns: 1fr;
            }

            .image-credit {
              position: relative;
              bottom: auto;
              right: auto;
              margin-top: 1rem;
              text-align: center;
            }
          }
        `}
      </style>
    </div>
  );
};

export default CityLandingPage;